import COS from "cos-js-sdk-v5";
import { createSDKI18n, resolveApiErrorMessage, type SDKI18n, type SDKLocale } from "../i18n";
import { notifyError, type XmaxErrorNotifier } from "../shared/error-utils";
import { createAbortError, isAbortError, throwIfAborted } from "../shared/abort";
import {
  attachErrorDiagnostics,
  type XmaxErrorCode,
  isXmaxSdkError,
  toXmaxSdkError,
  XmaxSdkError,
} from "../shared/sdk-error";
import type { CosImageCheckResult } from "./cos-media-check";
import { XMAX_SDK_BUILD_REGION, type XmaxSdkRegion } from "../build-config";
import { beginHttpTrace } from '../telemetry/http';
import { apiHost, emitTelemetry, telemetryEnvironment, telemetryId, telemetryReportSource, type XmaxReportSource, type XmaxTelemetryEventName, type XmaxTelemetryParams } from '../telemetry/collector';
import { diagnosticError } from '../telemetry/startup';
import { recordUploadedAssetEvent, registerUploadedAsset } from '../telemetry/assets';
import { safeMd5Hex } from '../telemetry/md5';

export type { XmaxSdkRegion } from "../build-config";

/** Region → official Open API v1 production base URL. */
export const XMAX_OPEN_API_BASE_URLS: Record<XmaxSdkRegion, string> = {
  // 国内 — @xmaxai/sdk
  cn: "https://api.xmaxai.com/open/api/v1",
  // 海外 — @xmaxai/sdk-global
  global: "https://api.xmax.ai/open/api/v1",
};

/**
 * Official Xmax Open Platform production API base URL (Open API v1) for the
 * region this package was built for:
 * - `@xmaxai/sdk` (cn):        https://api.xmaxai.com/open/api/v1
 * - `@xmaxai/sdk-global`:      https://api.xmax.ai/open/api/v1
 */
export const XMAX_OPEN_API_PRODUCTION_BASE_URL =
  XMAX_OPEN_API_BASE_URLS[XMAX_SDK_BUILD_REGION];

export const ACTIVE_STATUS = "ACTIVE";
const STS_TTL_MS = 30 * 60 * 1000;
const STS_REFRESH_BUFFER_MS = 5 * 60 * 1000;
const HEARTBEAT_CONSECUTIVE_FAILURE_LIMIT = 3;

function isBrowserFile(value: unknown): value is File {
  if (!value || typeof value !== "object") {
    return false;
  }
  if (typeof File !== "undefined" && value instanceof File) {
    return true;
  }
  // A File created by an iframe belongs to a different JavaScript realm, so
  // `instanceof File` is false even though it is valid for Blob uploads.
  return Object.prototype.toString.call(value) === "[object File]"
    && typeof (value as File).name === "string"
    && typeof (value as File).type === "string"
    && typeof (value as File).slice === "function";
}

function isBrowserBlob(value: unknown): value is Blob {
  if (!value || typeof value !== "object") {
    return false;
  }
  if (typeof Blob !== "undefined" && value instanceof Blob) {
    return true;
  }
  return Object.prototype.toString.call(value) === "[object Blob]"
    && typeof (value as Blob).type === "string"
    && typeof (value as Blob).slice === "function"
    && typeof (value as Blob).size === "number";
}

/** Accept File, cross-realm File, or Blob (normalized to File for COS upload). */
function normalizeUploadFile(value: unknown, expectedTypePrefix: string): File | null {
  if (isBrowserFile(value)) {
    return value;
  }
  if (!isBrowserBlob(value)) {
    return null;
  }
  const fallbackType = expectedTypePrefix.startsWith("video/")
    ? "video/mp4"
    : "image/jpeg";
  const type = value.type?.trim() || fallbackType;
  const extension = type.includes("/")
    ? type.split("/")[1]?.split("+")[0] || (expectedTypePrefix.startsWith("video/") ? "mp4" : "jpg")
    : (expectedTypePrefix.startsWith("video/") ? "mp4" : "jpg");
  return new File([value], `upload.${extension}`, { type });
}

export interface ModelExtra {
  room_id?: string;
  bot_name?: string;
  user_id?: string;
  rtc_app_id?: string;
  room_token?: string;
}

export interface Session {
  sessionUid: string;
  userUid?: string;
  modelTypeUid?: string;
  modelTypeName?: string;
  modelUid?: string;
  modelExtra?: ModelExtra;
  status?: string;
  lastHeartbeatTimestamp?: string;
  closeTimestamp?: string | null;
  closeReason?: string | null;
  createTimestamp?: string;
  updateTimestamp?: string;
}

export interface StsCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
}

export interface CosSts {
  type: string;
  bucket: string;
  region: string;
  /** Overseas upload host (optional HTTP(S) scheme); also used as a returned URL fallback. */
  endpoint: string;
  prefix: string;
  credentials: StsCredentials;
}

export interface UploadImageResult {
  key: string;
  url: string;
  raw: unknown;
  sts: CosSts;
}

export interface UploadVideoResult {
  key: string;
  url: string;
  raw: unknown;
  sts: CosSts;
}

export interface RtcJoinInfo {
  appId: string;
  roomId: string;
  userId: string;
  token: string;
  botName?: string;
}

export interface XmaxLogEntry {
  id: string;
  level: "info" | "success" | "warn" | "error";
  message: string;
  data?: unknown;
  timestamp: string;
}

export interface ClientOptions {
  apiKey?: string;
  /** Open API base URL, e.g. {@link XMAX_OPEN_API_PRODUCTION_BASE_URL}. */
  baseUrl: string;
  authToken?: string;
  timeoutMs?: number;
  locale?: SDKLocale;
  i18n?: SDKI18n;
  /** Receives asynchronous errors as `(message, XmaxSdkError)`. */
  onError?: XmaxErrorNotifier;
  /** Set false to disable SDK diagnostics for this client. */
  telemetry?: boolean;
  reportSource?: XmaxReportSource;
  /** @internal Correlates requests with a realtime startup. */
  telemetryContext?: () => XmaxTelemetryParams | undefined;
  /** @internal Records into a realtime trace so event ordering remains exact. */
  telemetryRecord?: (name: XmaxTelemetryEventName, params: XmaxTelemetryParams) => void;
}

export type RequestOptions = {
  signal?: AbortSignal;
};

function sanitizeFileName(fileName: string): string {
  return fileName
    .trim()
    .replace(/[^\w.\-一-龥]/g, "_")
    .replace(/_+/g, "_");
}

function buildCosFileUrl(endpoint: string, bucket: string, key: string): string {
  const trimmed = endpoint.trim();
  const url = new URL(
    /^https?:\/\//i.test(trimmed)
      ? (trimmed.endsWith("/") ? trimmed : `${trimmed}/`)
      : `https://${trimmed.replace(/^\/+/, "")}${trimmed.endsWith("/") ? "" : "/"}`,
  );
  if (url.hostname.startsWith("cos.") && !url.hostname.startsWith(`${bucket}.`)) {
    url.hostname = `${bucket}.${url.hostname}`;
  }
  return new URL(key.replace(/^\/+/, ""), url.toString()).toString();
}

/** Prefer COS putObject Location; fall back to STS endpoint or bucket/region URL. */
function resolveCosUploadUrl(raw: unknown, sts: CosSts, key: string): string {
  const location = (raw as { Location?: string } | null)?.Location?.trim();
  if (location) {
    return /^https?:\/\//i.test(location)
      ? location
      : `https://${location.replace(/^\/+/, "")}`;
  }

  const endpoint = sts.endpoint?.trim();
  if (endpoint) {
    return buildCosFileUrl(endpoint, sts.bucket, key);
  }

  return `https://${sts.bucket}.cos.${sts.region}.myqcloud.com/${key.replace(/^\/+/, "")}`;
}

function resolvePublicApiErrorCode(code: unknown): XmaxErrorCode {
  const normalized = code === null || code === undefined ? '' : String(code).trim();
  switch (normalized) {
    case '46001':
    case '46002':
    case '46018':
    case '46019':
    case '46021':
      return 'INVALID_API_KEY';
    case '46003':
    case '46005':
      return 'INVALID_MODEL';
    case '46004':
    case '40030':
    case '40032':
    case '40034':
    case '40035':
    case '40038':
    case '40039':
    case '40040':
    case '40041':
    case '40043':
    case '40044':
    case '40046':
    case '40047':
    case '46023':
    case '46026':
    case '46027':
      return 'INVALID_INPUT';
    case '40033':
    case '40036':
      return 'UNSUPPORTED_MEDIA';
    case '40037':
      return 'MEDIA_PROCESSING_ERROR';
    case '46012':
    case '46013':
    case '46014':
    case '46015':
      return 'SESSION_ERROR';
    default:
      return 'API_ERROR';
  }
}

export class XmaxOpenClient {
  private readonly apiKey: string | undefined;
  private readonly authToken: string | undefined;
  private readonly baseUrl: string;
  private readonly timeoutMs: number;
  private readonly telemetry: boolean | undefined;
  private readonly telemetryContext: ClientOptions['telemetryContext'];
  private readonly telemetryRecord: ClientOptions['telemetryRecord'];
  private readonly telemetryBase: XmaxTelemetryParams;
  private readonly i18n: SDKI18n;
  private readonly onError: XmaxErrorNotifier | undefined;
  private stsCache: { value: CosSts; fetchedAt: number } | null = null;
  private heartbeatIntervalId: number | null = null;
  private heartbeatGeneration = 0;
  private suppressErrorNotification = false;
  private teardownAbortController: AbortController | null = null;
  private readonly pendingRequestControllers = new Set<AbortController>();

  constructor(options: ClientOptions) {
    const i18n = options.i18n ?? createSDKI18n({ locale: options.locale });
    const apiKey = options.apiKey?.trim();
    const authToken = options.authToken?.trim();
    if (!apiKey && !authToken) {
      throw new XmaxSdkError("INVALID_API_KEY", i18n.t("errors.apiKeyEmpty"));
    }
    if (!options.baseUrl?.trim()) {
      throw new XmaxSdkError("INVALID_INPUT", i18n.t("errors.baseUrlEmpty"));
    }
    this.apiKey = apiKey;
    this.authToken = authToken;
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.timeoutMs = options.timeoutMs ?? 10_000;
    this.telemetry = options.telemetry;
    this.telemetryContext = options.telemetryContext;
    this.telemetryRecord = options.telemetryRecord;
    const reportSource = telemetryReportSource(options.reportSource);
    this.telemetryBase = {
      api_key_md5: options.telemetry === false ? undefined : safeMd5Hex(apiKey ?? authToken ?? ''),
      api_host: apiHost(this.baseUrl),
      env: telemetryEnvironment(reportSource),
      report_source: reportSource,
    };
    this.i18n = i18n;
    this.onError = options.onError;
  }

  private throwError(
    code: XmaxErrorCode,
    message: string,
  ): never {
    const error = new XmaxSdkError(code, message);
    if (this.suppressErrorNotification) {
      throw error;
    }
    throw notifyError(error, this.onError, message);
  }

  /** Stop heartbeat, abort in-flight API requests, and suppress error notifications during teardown. */
  beginTeardown(): void {
    this.suppressErrorNotification = true;
    this.stopHeartbeat();
    if (this.teardownAbortController) {
      this.teardownAbortController.abort();
    }
    this.teardownAbortController = new AbortController();
    this.pendingRequestControllers.forEach((controller) => {
      controller.abort();
    });
    this.pendingRequestControllers.clear();
  }

  endTeardown(): void {
    this.suppressErrorNotification = false;
    this.teardownAbortController = null;
  }

  /** Normalize browser-specific fetch failures (Chrome, Safari, Firefox). */
  private isNetworkError(error: unknown): boolean {
    if (!(error instanceof Error)) {
      return false;
    }
    return /failed to fetch|fetch failed|load failed|network\s*(?:request\s*)?(?:error|failed)/i.test(error.message);
  }

  async request<T>(
    method: string,
    path: string,
    body?: unknown,
    options: RequestOptions = {},
    telemetryParams?: XmaxTelemetryParams,
  ): Promise<T> {
    const assetOperationId = typeof telemetryParams?.operation_id === 'string'
      ? telemetryParams.operation_id
      : undefined;
    const telemetryRecord = assetOperationId
      ? (name: XmaxTelemetryEventName, params: XmaxTelemetryParams) => {
          recordUploadedAssetEvent(assetOperationId, name, params);
          if (this.telemetryRecord) this.telemetryRecord(name, params);
          else emitTelemetry(name, params);
        }
      : this.telemetryRecord;
    const diagnostic = beginHttpTrace(
      this.telemetry,
      method,
      path,
      this.baseUrl,
      this.timeoutMs,
      () => ({ ...this.telemetryBase, ...this.telemetryContext?.(), ...telemetryParams }),
      telemetryRecord,
    );
    try { throwIfAborted(options.signal); } catch (error) {
      diagnostic?.error(error, 'cancelled', 'pre_aborted');
      diagnostic?.finish();
      throw error;
    }
    const controller = new AbortController();
    this.pendingRequestControllers.add(controller);

    const onTeardownAbort = () => controller.abort();
    const onExternalAbort = () => controller.abort();
    this.teardownAbortController?.signal.addEventListener("abort", onTeardownAbort);
    options.signal?.addEventListener("abort", onExternalAbort, { once: true });
    if (options.signal?.aborted) {
      controller.abort();
    }

    let requestTimedOut = false;
    let responseStatus: number | undefined;
    let responseOk: boolean | undefined;
    let responseParsed = false;
    let responsePayloadValid = false;
    let responseBusinessCode: string | undefined;
    const timeoutId = globalThis.setTimeout(() => {
      requestTimedOut = true;
      controller.abort();
    }, this.timeoutMs);

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    if (this.apiKey) {
      headers["X-Api-Key"] = this.apiKey;
    }

    if (this.authToken) {
      headers["Authorization"] = `Bearer ${this.authToken}`;
    }

    try {
      diagnostic?.sending();
      const response = await fetch(`${this.baseUrl}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: controller.signal,
      });
      responseStatus = response.status;
      responseOk = response.ok;
      diagnostic?.headers(response.status);
      const text = await response.text();
      diagnostic?.body();
      let payload: unknown;

      try {
        payload = text ? JSON.parse(text) : null;
        responseParsed = true;
      } catch {
        throw new XmaxSdkError("API_ERROR", this.i18n.t("errors.invalidJson"));
      }

      if (typeof payload !== "object" || payload === null) {
        throw new XmaxSdkError("API_ERROR", this.i18n.t("errors.invalidPayload"));
      }
      responsePayloadValid = true;

      const p = payload as Record<string, unknown>;
      diagnostic?.businessCode(p.code);
      responseBusinessCode = typeof p.code === 'string' || typeof p.code === 'number'
        ? String(p.code)
        : undefined;

      if (response.ok && p.success === true) {
        return p.data as T;
      }

      // Prefer localized message for known business codes; otherwise use the
      // backend message when present; only then fall back to a generic retry hint.
      const mappedMessage = resolveApiErrorMessage(p.code as string | number | null | undefined, this.i18n.locale);
      const backendMessage =
        typeof p.message === "string" && p.message.trim() ? p.message.trim() : undefined;
      const errorMessage = mappedMessage ?? backendMessage ?? this.i18n.t("errors.requestRetry");
      throw attachErrorDiagnostics(
        new XmaxSdkError(resolvePublicApiErrorCode(p.code), errorMessage),
        {
          failure_type: response.ok ? 'business_error' : 'http_error',
          business_code: responseBusinessCode,
          http_status: response.status,
        },
      );
    } catch (error) {
      const requestDiagnostics = {
        failure_type: requestTimedOut
          ? 'timeout' as const
          : options.signal?.aborted || controller.signal.aborted
            ? 'cancelled' as const
            : responseStatus === undefined
              ? 'network_error' as const
              : responseOk === false
                ? 'http_error' as const
                : !responseParsed || !responsePayloadValid
                  ? 'invalid_response' as const
                  : 'business_error' as const,
        business_code: responseBusinessCode,
        http_status: responseStatus,
      };
      attachErrorDiagnostics(error, requestDiagnostics);
      diagnostic?.error(error, requestTimedOut ? 'timeout' : controller.signal.aborted ? 'cancelled' : 'error',
        requestTimedOut ? 'request_timeout' : options.signal?.aborted ? 'external_abort' : controller.signal.aborted ? 'session_teardown' : undefined);
      if (isXmaxSdkError(error)) {
        if (this.suppressErrorNotification) {
          throw error;
        }
        throw notifyError(error, this.onError, {
          code: "API_ERROR",
          message: error.message || this.i18n.t("errors.requestFailed"),
        });
      } else if (isAbortError(error)) {
        if (options.signal?.aborted && !requestTimedOut) {
          throw error;
        }
        const normalized = attachErrorDiagnostics(
          new XmaxSdkError("NETWORK_ERROR", this.i18n.t("errors.requestTimeout")),
          requestDiagnostics,
        );
        if (this.suppressErrorNotification) {
          throw normalized;
        }
        throw notifyError(normalized, this.onError, this.i18n.t("errors.requestTimeout"));
      } else if (this.isNetworkError(error)) {
        const normalized = attachErrorDiagnostics(
          new XmaxSdkError("NETWORK_ERROR", this.i18n.t("errors.networkError")),
          requestDiagnostics,
        );
        if (this.suppressErrorNotification) {
          throw normalized;
        }
        throw notifyError(normalized, this.onError, this.i18n.t("errors.networkError"));
      } else if (error instanceof Error) {
        const normalized = attachErrorDiagnostics(toXmaxSdkError(error, {
          code: "API_ERROR",
          message: error.message || this.i18n.t("errors.requestFailed"),
        }), requestDiagnostics);
        if (this.suppressErrorNotification) {
          throw normalized;
        }
        throw notifyError(normalized, this.onError, {
          code: "API_ERROR",
          message: error.message || this.i18n.t("errors.requestFailed"),
        });
      } else {
        const normalized = attachErrorDiagnostics(
          new XmaxSdkError("API_ERROR", this.i18n.t("errors.requestFailed")),
          requestDiagnostics,
        );
        if (this.suppressErrorNotification) {
          throw normalized;
        }
        throw notifyError(normalized, this.onError, this.i18n.t("errors.requestFailed"));
      }
    } finally {
      diagnostic?.finish();
      this.teardownAbortController?.signal.removeEventListener("abort", onTeardownAbort);
      options.signal?.removeEventListener("abort", onExternalAbort);
      this.pendingRequestControllers.delete(controller);
      globalThis.clearTimeout(timeoutId);
    }
  }

  async createSession(model: string): Promise<Session> {
    if (!model.trim()) {
      this.throwError("INVALID_MODEL", this.i18n.t("errors.modelEmpty"));
    }
    return this.request<Session>("POST", "/session", { model });
  }

  async getSession(sessionUid: string): Promise<Session> {
    return this.request<Session>("GET", `/session/${sessionUid}`);
  }

  async heartbeatSession(sessionUid: string): Promise<Session> {
    return this.request<Session>("PUT", `/session/${sessionUid}/heartbeat`);
  }

  async closeSession(sessionUid: string): Promise<Session | null> {
    const previousSuppress = this.suppressErrorNotification;
    this.suppressErrorNotification = true;
    try {
      return await this.request<Session>("DELETE", `/session/${sessionUid}`);
    } catch {
      return null;
    } finally {
      this.suppressErrorNotification = previousSuppress;
    }
  }

  async getCosSts(
    forceRefresh = false,
    options: RequestOptions = {},
    telemetryParams?: XmaxTelemetryParams,
  ): Promise<CosSts> {
    throwIfAborted(options.signal);
    const now = performance.now();
    if (
      !forceRefresh &&
      this.stsCache &&
      now - this.stsCache.fetchedAt < STS_TTL_MS - STS_REFRESH_BUFFER_MS
    ) {
      return this.stsCache.value;
    }

    const sts = await this.request<CosSts>("GET", "/cos/sts", undefined, options, telemetryParams);
    throwIfAborted(options.signal);
    this.stsCache = {
      value: sts,
      fetchedAt: now,
    };
    return sts;
  }

  /** Only overseas builds use the upload endpoint supplied by the STS service. */
  private resolveCosUploadEndpoint(
    sts: CosSts,
  ): { Domain?: string; Protocol?: "https:" } {
    if (XMAX_SDK_BUILD_REGION !== "global") {
      return {};
    }

    const endpoint = sts.endpoint?.trim();
    if (!endpoint) {
      return {};
    }

    let url: URL;
    try {
      const value = endpoint.startsWith("//")
        ? `https:${endpoint}`
        : /^[a-z][a-z0-9+.-]*:/i.test(endpoint)
          ? endpoint
          : `https://${endpoint}`;
      url = new URL(value);
    } catch {
      return this.throwError("INVALID_INPUT", "Invalid COS upload endpoint");
    }

    if (
      !["https:", "http:"].includes(url.protocol) ||
      !url.hostname ||
      url.username ||
      url.password ||
      url.port ||
      url.pathname !== "/" ||
      url.search ||
      url.hash
    ) {
      return this.throwError(
        "INVALID_INPUT",
        "COS upload endpoint must contain only a hostname",
      );
    }

    let domain = url.hostname;
    // Region-level endpoints omit the bucket required by COS virtual-hosted requests.
    if (/^cos\.[a-z0-9-]+\.myqcloud\.com$/i.test(domain)) {
      domain = `${sts.bucket}.${domain}`;
    }
    return { Domain: domain, Protocol: "https:" };
  }

  private async uploadObject(
    file: File,
    expectedTypePrefix: string,
    options: RequestOptions = {},
  ): Promise<{
    key: string;
    url: string;
    raw: unknown;
    sts: CosSts;
  }> {
    throwIfAborted(options.signal);
    const uploadFile = normalizeUploadFile(file, expectedTypePrefix);
    if (!uploadFile) {
      this.throwError("INVALID_INPUT", this.i18n.t("errors.invalidFile"));
    }

    if (!uploadFile.type.startsWith(expectedTypePrefix)) {
      this.throwError("UNSUPPORTED_MEDIA", this.i18n.t("errors.invalidFileType", { type: uploadFile.type || "(empty)" }));
    }

    const operationId = telemetryId();
    const stsCacheHit = Boolean(
      this.stsCache
      && performance.now() - this.stsCache.fetchedAt < STS_TTL_MS - STS_REFRESH_BUFFER_MS
    );
    const sts = await this.getCosSts(false, options, { operation_id: operationId });
    throwIfAborted(options.signal);
    const safeFileName = sanitizeFileName(uploadFile.name) || "image";
    const key = `${sts.prefix}${Date.now()}_${safeFileName}`;

    const cos = new COS({
      SecretId: sts.credentials.accessKeyId,
      SecretKey: sts.credentials.secretAccessKey,
      SecurityToken: sts.credentials.sessionToken,
      ...this.resolveCosUploadEndpoint(sts),
    });

    const assetType = expectedTypePrefix.startsWith("video/") ? "video" : "image";
    const uploadStartedAt = performance.now();
    const recordUpload = (name: 'sdk_asset_upload_start' | 'sdk_asset_upload_end', params: XmaxTelemetryParams) => {
      if (this.telemetry === false) return;
      const eventParams = { ...this.telemetryBase, ...this.telemetryContext?.(), ...params };
      recordUploadedAssetEvent(operationId, name, eventParams);
      if (this.telemetryRecord) this.telemetryRecord(name, params);
      else emitTelemetry(name, eventParams);
    };
    recordUpload('sdk_asset_upload_start', {
      operation_id: operationId,
      asset_type: assetType,
      size_bytes: uploadFile.size,
      sts_cache_hit: stsCacheHit,
    });

    let raw: unknown;
    try {
      raw = await new Promise<unknown>((resolve, reject) => {
        let settled = false;
        let taskId: COS.TaskId | undefined;

        const cleanup = () => {
          options.signal?.removeEventListener("abort", onAbort);
        };
        const cancelUpload = () => {
          if (taskId === undefined) return;
          try {
            cos.cancelTask(taskId);
          } catch {
            // The task may have completed between the abort event and cancelTask.
          }
        };
        const onAbort = () => {
          if (settled) return;
          settled = true;
          cancelUpload();
          cleanup();
          reject(createAbortError());
        };

        options.signal?.addEventListener("abort", onAbort, { once: true });
        if (options.signal?.aborted) {
          onAbort();
        }
        if (settled) return;

        try {
          cos.putObject(
            {
              Bucket: sts.bucket,
              Region: sts.region,
              Key: key,
              Body: uploadFile,
              ContentType: uploadFile.type,
              onTaskReady: (nextTaskId) => {
                taskId = nextTaskId;
                if (options.signal?.aborted) {
                  cancelUpload();
                }
              },
            },
            (error, data) => {
              if (settled) return;
              settled = true;
              cleanup();
              if (error) {
                reject(error);
                return;
              }
              resolve(data);
            },
          );
        } catch (error) {
          if (settled) return;
          settled = true;
          cleanup();
          reject(error);
        }
      });
    } catch (error) {
      recordUpload('sdk_asset_upload_end', {
        operation_id: operationId,
        asset_type: assetType,
        size_bytes: uploadFile.size,
        ok: false,
        duration_ms: Math.round(performance.now() - uploadStartedAt),
        ...diagnosticError(error),
      });
      if (isAbortError(error)) {
        throw error;
      }
      throw this.throwError(
        "API_ERROR",
        error instanceof Error && error.message ? error.message : this.i18n.t("errors.requestFailed"),
      );
    }

    try {
      const result = {
        key,
        url: resolveCosUploadUrl(raw, sts, key),
        raw,
        sts,
      };
      recordUpload('sdk_asset_upload_end', {
        operation_id: operationId,
        asset_type: assetType,
        size_bytes: uploadFile.size,
        ok: true,
        duration_ms: Math.round(performance.now() - uploadStartedAt),
      });
      registerUploadedAsset(result.url, operationId);
      return result;
    } catch (error) {
      recordUpload('sdk_asset_upload_end', {
        operation_id: operationId,
        asset_type: assetType,
        size_bytes: uploadFile.size,
        ok: false,
        duration_ms: Math.round(performance.now() - uploadStartedAt),
        ...diagnosticError(error),
      });
      throw this.throwError(
        "API_ERROR",
        this.i18n.t("errors.requestFailed"),
      );
    }
  }

  async uploadImage(file: File, options?: RequestOptions): Promise<UploadImageResult> {
    return this.uploadObject(file, "image/", options);
  }

  async uploadVideo(file: File, options?: RequestOptions): Promise<UploadVideoResult> {
    return this.uploadObject(file, "video/", options);
  }

  /**
   * Upload an image then run `/cos/image/check`.
   * Returns the same upload metadata as {@link uploadImage}, with the checked URL.
   */
  async uploadAndCheckImage(file: File, options?: RequestOptions): Promise<UploadImageResult> {
    const uploaded = options
      ? await this.uploadImage(file, options)
      : await this.uploadImage(file);
    const data = await this.request<CosImageCheckResult>("POST", "/cos/image/check", {
      url: uploaded.url,
    }, options);

    if (!data?.url) {
      this.throwError("API_ERROR", this.i18n.t("errors.imageCheckMissingUrl"));
    }

    if (data.safe === false) {
      this.throwError("UNSUPPORTED_MEDIA", this.i18n.t("errors.imageCheckUnsafe"));
    }

    return {
      ...uploaded,
      url: data.url,
    };
  }

  startHeartbeat(
    sessionUid: string,
    intervalMs: number = 10000,
    onHeartbeat?: (session: Session, diagnostics?: XmaxTelemetryParams) => void,
    onError?: (error: XmaxSdkError, diagnostics?: XmaxTelemetryParams) => void,
  ): void {
    this.stopHeartbeat();
    const generation = this.heartbeatGeneration;
    let consecutiveFailures = 0;
    let heartbeatInFlight = false;
    let lastSuccessAt: number | undefined;
    let attempts = 0;

    const heartbeat = async () => {
      if (heartbeatInFlight) {
        return;
      }
      heartbeatInFlight = true;
      attempts++;
      try {
        const session = await this.heartbeatSession(sessionUid);
        if (generation !== this.heartbeatGeneration) return;
        consecutiveFailures = 0;
        lastSuccessAt = performance.now();
        onHeartbeat?.(session, { heartbeat_attempts: attempts, heartbeat_consecutive_failures: 0, heartbeat_interval_ms: intervalMs });

        if (session.status !== ACTIVE_STATUS) {
          this.stopHeartbeat();
        }
      } catch (error) {
        if (generation !== this.heartbeatGeneration) return;
        consecutiveFailures += 1;
        const definitelyOffline =
          typeof navigator !== "undefined" && navigator.onLine === false;
        if (definitelyOffline || consecutiveFailures >= HEARTBEAT_CONSECUTIVE_FAILURE_LIMIT) {
          onError?.(toXmaxSdkError(error, {
            code: "SESSION_ERROR",
            message: this.i18n.t("errors.requestFailed"),
          }), {
            heartbeat_attempts: attempts,
            heartbeat_consecutive_failures: consecutiveFailures,
            heartbeat_interval_ms: intervalMs,
            heartbeat_last_success_age_ms: lastSuccessAt === undefined ? undefined : Math.round(performance.now() - lastSuccessAt),
            heartbeat_failure_trigger: definitelyOffline ? 'browser_offline' : 'consecutive_failures',
          });
          this.stopHeartbeat();
        }
      } finally {
        heartbeatInFlight = false;
      }
    };

    heartbeat();
    this.heartbeatIntervalId = window.setInterval(heartbeat, intervalMs);
  }

  stopHeartbeat(): void {
    this.heartbeatGeneration++;
    if (this.heartbeatIntervalId !== null) {
      clearInterval(this.heartbeatIntervalId);
      this.heartbeatIntervalId = null;
    }
  }

  getRtcJoinInfo(session: Session): RtcJoinInfo {
    const roomId = session.modelExtra?.room_id?.trim();
    const appId = session.modelExtra?.rtc_app_id?.trim();
    const token = session.modelExtra?.room_token?.trim();
    const userId = session.modelExtra?.user_id?.trim() || session.userUid?.trim() || "";

    if (!roomId || !appId || !token || !userId) {
      this.throwError("SESSION_ERROR", this.i18n.t("errors.sessionJoinInfoIncomplete"));
    }

    return {
      appId,
      roomId,
      userId,
      token,
      botName: session.modelExtra?.bot_name,
    };
  }
}
